import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Badge, Button, Card, Input, Modal } from "../../../../shared";
import { useDeleteCustomerMutation } from "../../../../redux/services/customers/customersApi";

const ACTIVE_BILLING_STATUSES = new Set([
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "incomplete",
]);

function hasActiveBilling(customer = {}) {
  const billing = customer?.billing || {};
  const status = String(billing.status || "").toLowerCase();

  return Boolean(
    billing.subscriptionId ||
      billing.customerId ||
      ACTIVE_BILLING_STATUSES.has(status)
  );
}

/**
 * Returns the tenant-level data that should be resolved before deleting a customer.
 * Keep this UI-side check aligned with the API delete blocker rules.
 */
export function getCustomerDeleteBlockers(customer = {}) {
  const blockers = [];
  const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];
  const tags = Array.isArray(customer?.tags) ? customer.tags : [];

  if (customer?.active) {
    blockers.push({
      id: "active",
      label: "Customer is active",
      description: "Deactivate the tenant before deleting it.",
      count: 1,
    });
  }

  if (contacts.length > 0) {
    blockers.push({
      id: "contacts",
      label: "Users are attached",
      description: "Remove or transfer tenant users before deletion.",
      count: contacts.length,
    });
  }

  if (hasActiveBilling(customer)) {
    blockers.push({
      id: "billing",
      label: "Billing is configured",
      description: "Cancel subscriptions and clear billing customer references.",
      count: 1,
    });
  }

  if (customer?.emailConfig?.enabled) {
    blockers.push({
      id: "emailConfig",
      label: "Email sending is enabled",
      description: "Disable tenant email configuration before deleting.",
      count: 1,
    });
  }

  if (customer?.metadata?.apiKeyHash || customer?.metadata?.apiKeyPreview) {
    blockers.push({
      id: "apiKey",
      label: "API key is active",
      description: "Revoke the tenant API key before deleting.",
      count: 1,
    });
  }

  if (tags.length > 0) {
    blockers.push({
      id: "tags",
      label: "Customer tags are assigned",
      description: "Review or clear tags used for customer segmentation.",
      count: tags.length,
    });
  }

  return blockers;
}

const DeleteCustomerCard = ({ customer }) => {
  const navigate = useNavigate();
  const [confirmValue, setConfirmValue] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteCustomer, { isLoading: isDeleting }] =
    useDeleteCustomerMutation();

  const blockers = useMemo(
    () => getCustomerDeleteBlockers(customer),
    [customer]
  );
  const hasBlockers = blockers.length > 0;
  const requiredConfirmation = customer?.name || customer?.slug || "DELETE";
  const canDelete =
    Boolean(customer?._id) &&
    !hasBlockers &&
    confirmValue.trim() === requiredConfirmation;

  function closeDeleteModal() {
    setDeleteModalVisible(false);
    setConfirmValue("");
  }

  async function handleDeleteCustomer() {
    if (!canDelete) return;

    try {
      const response = await deleteCustomer({ id: customer._id }).unwrap();
      toast(response?.message || "Customer deleted", {
        type: response?.status || "success",
        toastId: "customer-profile-delete-success",
      });
      closeDeleteModal();
      navigate("/admin/customers");
    } catch (error) {
      toast(error?.data?.message || "Unable to delete customer", {
        type: "error",
        toastId: "customer-profile-delete-error",
      });
    }
  }

  const deleteModalFooter = (
    <>
      <Button type="alternative" onClick={closeDeleteModal}>
        Cancel
      </Button>
      <Button
        type="danger"
        loading={isDeleting}
        disabled={!canDelete || isDeleting}
        onClick={handleDeleteCustomer}
      >
        Delete customer
      </Button>
    </>
  );

  return (
    <Card className="border-red-200">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-medium text-red-700">Danger Zone</h3>
          <p className="text-sm text-gray-500 mt-1">
            Delete this customer only after tenant data blockers are resolved.
            This action cannot be undone.
          </p>
        </div>
        <Badge
          color={hasBlockers ? "yellow" : "green"}
          label={hasBlockers ? `${blockers.length} blockers` : "Ready to delete"}
        />
      </div>

      <div className="mt-6 rounded-lg border border-gray-200">
        {hasBlockers ? (
          <div className="divide-y divide-gray-200">
            {blockers.map((blocker) => (
              <div
                key={blocker.id}
                className="flex items-start gap-3 p-4"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">
                      {blocker.label}
                    </p>
                    {blocker.count > 1 && (
                      <Badge color="gray" label={String(blocker.count)} />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {blocker.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-3 p-4">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                No customer data blockers detected
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Confirm the customer name before running the delete action.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="danger"
          disabled={!customer?._id || hasBlockers}
          onClick={() => setDeleteModalVisible(true)}
        >
          Delete customer
        </Button>
      </div>

      <Modal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        onClose={closeDeleteModal}
        title="Delete customer"
        size="md"
        footer={deleteModalFooter}
        footerPosition="right"
        content={
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-800">
                This permanently deletes {customer?.name || "this customer"}.
              </p>
              <p className="mt-1 text-sm text-red-700">
                Type{" "}
                <span className="font-semibold">{requiredConfirmation}</span>{" "}
                to confirm.
              </p>
            </div>
            <Input
              id="deleteConfirmation"
              label="Confirmation"
              value={confirmValue}
              onChange={({ value }) => setConfirmValue(value)}
              placeholder={requiredConfirmation}
            />
          </div>
        }
      />
    </Card>
  );
};

export default DeleteCustomerCard;
